// How an extracted value reads in the data panel, decided by its field key.
// The stored value never changes: this only shapes what is shown and what the
// copy button gives.
//
//   * amounts ("invoice_amount": 1180.0) read "1,180.00" and copy "1180.00";
//   * counts ("quantity": 1200) read "1,200";
//   * identifiers ("invoice_number": "INV-4408") are set in mono, so 0/O and
//     1/l are told apart;
//   * everything else is shown as stored.
//
// Keys that only look like amounts (a rate, a percentage, a line or page
// number, a year, a number of pages, a weight, a class) are left alone.

const NUMERIC = /^-?\d+(?:\.\d+)?$/
const LEAVE_KEY = /(percent|ratio|(^|_)(rate|year|zip|postal_code|phone|number|no|id|code|line|page)$)/i
// A key that ends in pages, weight or class ("total_pages", "gross_weight",
// "freight_class") is not money, though it has a money word in it.
const MONEY_KEY = /^(?!.*(^|_)(pages?|weight|class)$).*(amount|total|subtotal|price|cost|tax|balance|discount|fee|charge|credit|debit|paid|freight|shipping|handling|net|gross)/i
// Whole words only, so "discount" and "account" are not counts.
const COUNT_KEY = /(^|_)(qty|quantity|count|units|pieces)(_|$)/i
const ID_KEY = /(^|_)(number|no|id|ref|reference|code|sku|upc|gtin|ean|account|invoice|po|so|bol|tracking)$/i

const count = new Intl.NumberFormat("en-US", { maximumFractionDigits: 6 })

// The leaf key of a field path: "line_items.0.unit_price" -> "unit_price".
export function leafKey (path) {
  return String(path || "").split(".").filter(part => !/^\d+$/.test(part)).pop() || ""
}

// { text, copy, mono, numeric } for a raw value under a key. `copy` is the
// plain form for pasting into another system.
export function displayFor (key, raw) {
  const value = raw == null ? "" : String(raw).trim()
  const result = { text: value, copy: value, mono: false, numeric: false }
  if (value === "") return result

  if (NUMERIC.test(value) && !LEAVE_KEY.test(key)) {
    const number = Number(value)
    // Counts first: "total_units" is a count, not money.
    if (COUNT_KEY.test(key)) {
      return { text: count.format(number), copy: value, mono: false, numeric: true }
    }
    if (MONEY_KEY.test(key)) {
      // Keep every significant digit, including sub-cent prices and amounts
      // beyond Number's exact range. A display or copy must not change a value.
      const [whole, fraction = ""] = value.split(".")
      const decimals = fraction.replace(/0+$/, "").padEnd(2, "0")
      const copy = `${whole}.${decimals}`
      const text = `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${decimals}`
      return { text, copy, mono: false, numeric: true }
    }
  }

  // A reference ("INV-4408", "77-20418") or a long number ("880021457309"),
  // not a small ordinal ("3").
  const referenceLike = /\d/.test(value) && (/[A-Za-z\-/:#.]/.test(value) || /^\d{5,}$/.test(value))
  result.mono = ID_KEY.test(key) && referenceLike && value.length <= 48
  result.numeric = NUMERIC.test(value)
  return result
}
